import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { HttpClient } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import { environment } from '@environment';
import {
  EquipmentConfig,
  ExerciseMeta,
  FileUpload,
  LevelTexts,
  Metric,
  ReportItem,
  Scene,
  SceneConfig,
} from '@models';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { ContentService } from '@services';
import { EMPTY, forkJoin, map, pipe, switchMap } from 'rxjs';
import { sceneMeta } from '../../scenes/scene-meta';

const DEFAULT_SCENE = 'pegTransfer';

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
declare const require: any;

type SceneState = {
  data: Scene;
  screenshotsUpload: FileUpload[];
  videoUpload: FileUpload;
  feedback: string[];
  screenshots: Blob[];
  finalScreenShot: Blob;
  reportItems: ReportItem[];
  maxTime: number;
  taskListCompleted: boolean;
  allTasksSkipped: boolean;
  assessment: LevelTexts;
  sceneConfig: SceneConfig;
  exerciseMeta: ExerciseMeta;
  equipmentConfig: EquipmentConfig;
};

const initialState: SceneState = {
  data: {} as Scene,
  screenshotsUpload: [],
  videoUpload: {} as FileUpload,
  feedback: [],
  screenshots: [],
  finalScreenShot: null,
  reportItems: [],
  maxTime: 0,
  taskListCompleted: false,
  allTasksSkipped: false,
  assessment: {} as LevelTexts,
  sceneConfig: {} as SceneConfig,
  exerciseMeta: {} as ExerciseMeta,
  equipmentConfig: {} as EquipmentConfig,
};

export const SceneStore = signalStore(
  { providedIn: 'root' },
  withDevtools('scene'),
  withState(initialState),
  withComputed(({ data, sceneConfig }) => {
    const contentService = inject(ContentService);
    return {
      path: computed(() => getScenePath(data().identifier)),
      introHtml: computed(() =>
        data().identifier
          ? contentService.get(
              getScenePath(data().identifier) + '/html/intro.html',
            )
          : EMPTY,
      ),
      customIntroHtml: computed(() =>
        data().identifier
          ? sceneConfig().customIntro
            ? contentService
                .get(
                  getScenePath(data().identifier) +
                    '/html/custom-intro-view.html',
                )
                .pipe(
                  map((html) =>
                    html
                      .toString()
                      .replace(
                        '@path',
                        getScenePath(data().identifier) + '/html/images/',
                      ),
                  ),
                )
            : EMPTY
          : EMPTY,
      ),
    };
  }),
  withMethods((store) => {
    const http = inject(HttpClient);
    return {
      getSceneData: rxMethod<string>(
        pipe(
          map(
            (sceneIdentifier) =>
              sceneIdentifier ?? sceneMeta[DEFAULT_SCENE].identifier,
          ),
          switchMap((sceneIdentifier) =>
            forkJoin([
              http
                .get<Scene>(
                  environment.API_HOST + `scene/${sceneIdentifier}/details`,
                )
                .pipe(
                  tapResponse({
                    next: (data) =>
                      patchState(store, {
                        data,
                        reportItems: data.reportSections[0].reportItems,
                        maxTime: data.reportSections[0].reportItems.find(
                          (reportItem) =>
                            reportItem.name === 'Exercise duration',
                        )?.maxValue,
                      }),
                    error: (err) => console.error(err),
                  }),
                ),
              http
                .get<SceneConfig>(
                  getScenePath(sceneIdentifier) + '/config.json',
                )
                .pipe(
                  tapResponse({
                    next: (sceneConfig) =>
                      patchState(store, {
                        sceneConfig,
                      }),
                    error: (err) => console.error(err),
                  }),
                ),
              http
                .get<LevelTexts>(
                  getScenePath(sceneIdentifier) + '/assessment.json',
                )
                .pipe(
                  tapResponse({
                    next: (assessment) => patchState(store, { assessment }),
                    error: (err) => console.error(err),
                  }),
                ),
              http
                .get<ExerciseMeta>(
                  getScenePath(sceneIdentifier) + '/assets/exercise.json',
                )
                .pipe(
                  tapResponse({
                    next: (exerciseMeta) => patchState(store, { exerciseMeta }),
                    error: (err) => console.error(err),
                  }),
                ),
            ]),
          ),
        ),
      ),
      updateTaskstate(
        taskListCompleted: boolean,
        allTasksSkipped: boolean,
      ): void {
        patchState(
          store,
          (state): SceneState => ({
            ...state,
            taskListCompleted,
            allTasksSkipped,
          }),
        );
      },
      updateMetrics(metrics: Metric[]): void {
        const reportItems = store.reportItems()?.map((reportItem) => {
          let metric = null;
          let score = 0;
          if (metrics.length > 0) {
            metric = metrics.find(
              (metric) => metric.id === reportItem.identifier,
            );

            switch (reportItem.bestIs) {
              case 'MinValue': {
                score = Math.round(
                  Math.max(
                    0,
                    reportItem.maxScore -
                      (reportItem.maxScore *
                        Math.max(0, metric.value - reportItem.minValue)) /
                        (reportItem.maxValue - reportItem.minValue),
                  ),
                );
                break;
              }
              case 'MaxValue': {
                score = Math.round(
                  Math.min(
                    reportItem.maxScore,
                    reportItem.maxScore *
                      (Math.max(0, metric.value - reportItem.minValue) /
                        (reportItem.maxValue - reportItem.minValue)),
                  ),
                );
                break;
              }
            }
            const noScore =
              !store.taskListCompleted() ||
              store.allTasksSkipped() ||
              metric.value === null;
            return {
              ...reportItem,
              id: reportItem.identifier,
              value: Math.round(metric.value),
              goalValue: reportItem.goalValueString,
              score: noScore ? 0 : Math.round(score),
              isVisible: true,
              noScore,
            };
          } else {
            return reportItem;
          }
        });
        patchState(
          store,
          (state): SceneState => ({
            ...state,
            reportItems,
          }),
        );
      },
      addScreenshot(screenshot: Blob): void {
        patchState(
          store,
          (state): SceneState => ({
            ...state,
            screenshots: [...state.screenshots, screenshot],
          }),
        );
      },
      setFinalScreenShot(finalScreenShot: Blob): void {
        patchState(
          store,
          (state): SceneState => ({
            ...state,
            finalScreenShot,
          }),
        );
      },
      setUploadVideo(videoUpload: FileUpload): void {
        patchState(
          store,
          (state): SceneState => ({
            ...state,
            videoUpload,
          }),
        );
      },
    };
  }),
);

const getScenePath = (sceneIdentifier: string): string => {
  switch (sceneIdentifier) {
    case '29184a66-cbba-4d71-9859-b1906e372d17': {
      return 'precision-cutting';
    }
    case '3f83544a-bb44-4fb2-950c-b3f07ed6a9e9': {
      return 'ligating-loop';
    }
    case '73d07b70-c5f1-4af3-bced-6b79b15eeb63': {
      return 'peg-transfer';
    }
    case 'a7d4b1ac-e4cc-4458-98b8-ad87156bb6dd': {
      return 'intracorporeal-knot';
    }
    case 'b788d589-4136-4e90-9172-3aa201096502': {
      return 'extracorporeal-knot';
    }
    case '4950f801-e7f1-42e8-a11a-b6e9738d0419': {
      return 'stratafix';
    }
    default:
      return null;
  }
};

export const createPath = (
  file: string,
  path: string,
  scenePath: string,
): string => {
  const paths = file.split('@shared/');
  if (paths.length > 1) {
    path = 'shared/' + paths[1];
  } else {
    path = scenePath + path + file;
  }
  return path;
};
